/**
 * Catalogue Config Context - Single Source of Truth
 * Prevents multiple API calls by centralizing config data management
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCatalogueConfig } from '../api/shop/products/productService';
import { CatalogueConfig } from '../api/shop/types/productTypes';

interface CatalogueConfigContextType {
  config: CatalogueConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const CatalogueConfigContext = createContext<CatalogueConfigContextType | undefined>(undefined);

interface CatalogueConfigProviderProps {
  children: ReactNode;
}

export const CatalogueConfigProvider: React.FC<CatalogueConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<CatalogueConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const configData = await getCatalogueConfig();
      setConfig(configData);
    } catch (err) {
      console.error('Error loading catalogue config:', err);
      setError('Failed to load configuration data');
    } finally {
      setLoading(false);
    }
  };

  const refetch = async () => {
    await fetchConfig();
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const value: CatalogueConfigContextType = {
    config,
    loading,
    error,
    refetch
  };

  return (
    <CatalogueConfigContext.Provider value={value}>
      {children}
    </CatalogueConfigContext.Provider>
  );
};

export const useCatalogueConfig = (): CatalogueConfigContextType => {
  const context = useContext(CatalogueConfigContext);
  if (context === undefined) {
    throw new Error('useCatalogueConfig must be used within a CatalogueConfigProvider');
  }
  return context;
};